import { AlpineComponent } from 'alpinejs';
import { Maybe } from 'typescript-functional-extensions';

import { alpineComponentNoStore } from '@/src/utilities/component-utilities';

export const imageCarouselComponent =
  alpineComponentNoStore<ImageCarouselComponent>('imageCarousel', factory);

function factory() {
  return {
    container: undefined,
    prev: undefined,
    next: undefined,
    initialized: false,
    index: 1,
    init() {
      this.container = this.$refs['container'];

      if (!this.container) {
        console.error('Missing carousel container element');

        return;
      }

      requestAnimationFrame(() => this.update());

      /**
       * Ensures initialization is marked only after initial rendering
       */

      this.container.addEventListener('scroll', this.update.bind(this), {
        passive: true,
      });

      setTimeout(() => (this.initialized = true), 0);
    },

    update() {
      if (!this.container) {
        console.error('Missing carousel container element');

        return;
      }

      const rect = this.container?.getBoundingClientRect();

      const visibleElements = Array.from(this.container.children).filter(
        (child) => {
          const childRect = child.getBoundingClientRect();
          return (
            childRect.left >= rect.left && childRect.right <= rect.right + 5 // accounts for last image not scrolling into center
          );
        }
      );

      if (visibleElements.length > 0) {
        this.prev = this.getPrevElement(visibleElements);
        this.next = this.getNextElement(visibleElements);

        Maybe.tryFirst(visibleElements)
          .map((el) => el.getAttribute('data-index') ?? '0')
          .map((indexStr) => parseInt(indexStr, 10))
          .execute((index) => {
            if (index) {
              this.index = index;
            }
          });
      }
    },

    getPrevElement(list) {
      const sibling = list[0]?.previousElementSibling;

      if (sibling instanceof HTMLElement) {
        return sibling;
      }

      if (this.initialized) {
        this.$refs['nextBtn']?.focus();
      }

      return undefined;
    },

    getNextElement(list) {
      const sibling = list[list.length - 1]?.nextElementSibling;

      if (sibling instanceof HTMLElement) {
        return sibling;
      }

      if (this.initialized) {
        this.$refs['prevBtn']?.focus();
      }

      return undefined;
    },

    scrollTo(element) {
      const current = this.container;

      if (!current || !(element instanceof HTMLElement)) {
        return;
      }

      const nextScrollPosition =
        element.offsetLeft +
        element.getBoundingClientRect().width / 2 -
        current.getBoundingClientRect().width / 2 -
        current.offsetLeft;

      current.scroll({
        left: nextScrollPosition,
        behavior: 'smooth',
      });
    },
  } as ImageCarouselComponent;
}

interface ImageCarouselComponent extends AlpineComponent {
  /**
   * A reference to the element containing all the images of the carousel
   */
  container: HTMLElement | undefined;
  /**
   * The previous image in the carousel (from the current)
   */
  prev: Element | undefined;
  /**
   * The next image in the carousel (from the current)
   */
  next: Element | undefined;
  /**
   * The index of the image currently displayed by the carousel (base 1)
   */
  index: number;
  /**
   * True if the component has been initialized and is ready for user interaction
   */
  initialized: boolean;

  update(): void;
  getPrevElement(list: Element[]): Element | undefined;
  getNextElement(list: Element[]): Element | undefined;
  scrollTo(element: Element | undefined): void;
  onFocus(elName: 'prev' | 'next'): void;
}
